import { memo, useMemo, useCallback, useRef, useEffect, useState } from "react";
import { ScrollView, ActivityIndicator } from "react-native";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import { format, getHours, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { Clip } from "../types/clip";
import { useDayClipCounts, type HourBucket } from "../hooks/useDayClips";
import Animated, { FadeInLeft, FadeInRight, FadeOut } from "react-native-reanimated";
import { ScalePressable } from "@src/components/ScalePressable";
import Svg, { Rect } from "react-native-svg";
import { isWeb } from "@src/utils/web-helpers";

const ITEM_WIDTH = 40;
const ITEM_MARGIN_RIGHT = 4; // theme.spacing.sm is typically 4
const HOURS = Array.from({ length: 24 }, (_, i) => i);

type ActivityHeatmapProps = {
  selectedDate: Date;
  selectedHour: number;
  onHourSelect: (hour: number) => void;
  // New interface: pre-computed hour buckets
  hourBuckets?: HourBucket[];
  // Loading status for the heatmap
  isLoading?: boolean;
  // Legacy interface: raw clips (will compute buckets internally)
  clips?: Clip[];
  dateDirection?: "forward" | "backward";
  timezone?: string;
};

function formatHour(hour: number): string {
  if (hour === 0) {
    return "12am";
  }
  if (hour < 12) {
    return `${hour}am`;
  }
  if (hour === 12) {
    return "12pm";
  }
  return `${hour - 12}pm`;
}

// Activity intensity thresholds
const HIGH_ACTIVITY_THRESHOLD = 40;

type ActivityLevel = "none" | "normal" | "high";

function getActivityLevel({ clipCount }: { clipCount: number }): ActivityLevel {
  if (clipCount === 0) return "none";
  if (clipCount >= HIGH_ACTIVITY_THRESHOLD) return "high";
  return "normal";
}

type ActivityColors = { none: string; low: string; high: string };

function getActivityColor({ activityLevel, colors }: { activityLevel: ActivityLevel; colors: ActivityColors }) {
  if (activityLevel === "none") return colors.none;
  if (activityLevel === "high") return colors.high;
  return colors.low;
}

export const ActivityHeatmap = memo(
  ({
    selectedDate,
    selectedHour,
    onHourSelect,
    hourBuckets,
    isLoading,
    clips,
    dateDirection,
    timezone,
  }: ActivityHeatmapProps) => {
    const { theme } = useUnistyles();
    const scrollViewRef = useRef<ScrollView>(null);
    const [scrollViewWidth, setScrollViewWidth] = useState(0);
    const lastScrollKeyRef = useRef<string | null>(null);

    // Use hourBuckets if provided, otherwise compute from clips (legacy)
    const clipsByHour = useMemo(() => {
      if (hourBuckets) {
        const hourMap = new Map<number, number>();
        for (const bucket of hourBuckets) {
          hourMap.set(bucket.hour, bucket.count);
        }
        return hourMap;
      }

      // Legacy: compute from clips
      const hourMap = new Map<number, number>();
      if (clips) {
        for (const clip of clips) {
          const hour = getHours(new Date(clip.timestamp));
          hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
        }
      }
      return hourMap;
    }, [hourBuckets, clips]);

    // FIXME: hour buckets don't contain all clips for the day
    const totalClipsForDay = useMemo(() => {
      if (hourBuckets) {
        return hourBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
      }
      if (clips) {
        return clips.length;
      }
      return Array.from(clipsByHour.values()).reduce((sum, count) => sum + count, 0);
    }, [hourBuckets, clips, clipsByHour]);

    const handleHourPress = useCallback(
      (hour: number) => {
        onHourSelect(hour);
      },
      [onHourSelect],
    );

    // Scroll to selected hour on mount and when date changes
    useEffect(() => {
      lastScrollKeyRef.current = null;
    }, [selectedDate]);

    useEffect(() => {
      const dateKey = selectedDate.toDateString();
      const scrollKey = `${dateKey}-${selectedHour}`;
      if (scrollViewRef.current && scrollViewWidth && lastScrollKeyRef.current !== scrollKey) {
        lastScrollKeyRef.current = scrollKey;
        const itemTotalWidth = ITEM_WIDTH + ITEM_MARGIN_RIGHT;
        const targetCenter = selectedHour * itemTotalWidth + ITEM_WIDTH / 2;
        const scrollX = Math.max(0, targetCenter - scrollViewWidth / 2);
        scrollViewRef.current.scrollTo({ x: scrollX, animated: true });
      }
    }, [selectedDate, selectedHour, scrollViewWidth]);

    const isHeatmapLoading = Boolean(isLoading);
    const enteringAnimation = dateDirection === "forward" ? FadeInRight : FadeInLeft;
    const exitingAnimation = FadeOut;

    return (
      <View mt="md">
        <View flexDirection="row" alignItems="center" justifyContent="space-between" mb="xl">
          <View flexDirection="row" alignItems="center" gap="sm">
            <Text textStyle="xs/semibold">{format(selectedDate, "EEEE, MMM d")}</Text>
            {timezone && (
              <Text textStyle="xs/regular" color="textSecondary">
                ({timezone})
              </Text>
            )}
          </View>
          <View flexDirection="row" alignItems="center" gap="sm">
            {isHeatmapLoading ?
              <ActivityIndicator size="small" color={theme.colors.text as string} />
            : <Text textStyle="xs/regular" color="textSecondary">
                {`${totalClipsForDay} clips today`}
              </Text>
            }
          </View>
        </View>
        <Animated.View entering={enteringAnimation} exiting={exitingAnimation} key={selectedDate.toISOString()}>
          <View pr="xxs">
            <ScrollView
              ref={scrollViewRef}
              horizontal
              contentContainerStyle={styles.scrollView}
              onLayout={(event) => {
                setScrollViewWidth(event.nativeEvent.layout.width);
              }}
            >
              {HOURS.map((hour) => {
                const clipCount = clipsByHour.get(hour) || 0;
                const activityLevel = getActivityLevel({ clipCount });
                const hasClips = activityLevel !== "none";
                const isSelected = selectedHour === hour;

                return (
                  <ScalePressable key={hour} onPress={() => handleHourPress(hour)}>
                    <View
                      w={40}
                      h={32}
                      rounded={theme.spacing.xs}
                      bg="surfaceCard"
                      mr="sm"
                      alignItems="center"
                      justifyContent="center"
                      gap="xl"
                    >
                      <View
                        rounded={theme.spacing.sm}
                        style={{
                          borderWidth: isSelected ? 2 : 0,
                          borderColor: isSelected ? theme.colors.interactive.buttons.default_background : "transparent",
                          padding: 1,
                        }}
                      >
                        <View
                          w={40}
                          h={26}
                          rounded={theme.spacing.xxs}
                          alignItems="center"
                          justifyContent="center"
                          style={{
                            backgroundColor: getActivityColor({
                              activityLevel,
                              colors: {
                                none: theme.colors.surface1.foregroundTertiary,
                                low: theme.colors.danger[400],
                                high: theme.colors.danger[700],
                              },
                            }),
                          }}
                        >
                          {hasClips && <Icon name="warningOutline" size={15} color={theme.colors.white.default} />}
                        </View>
                      </View>
                      <View
                        justifyContent="center"
                        alignItems="center"
                        w={40}
                        h={20}
                        rounded={theme.spacing.xxs}
                        style={{
                          backgroundColor:
                            isSelected ? theme.colors.interactive.buttons.default_background : "transparent",
                        }}
                      >
                        <Text
                          textStyle="xxs/regular"
                          style={{ color: isSelected ? theme.colors.white.default : theme.colors.text }}
                          numberOfLines={1}
                        >
                          {formatHour(hour)}
                        </Text>
                      </View>
                    </View>
                  </ScalePressable>
                );
              })}
            </ScrollView>
          </View>
        </Animated.View>
      </View>
    );
  },
);

// ============================================================================
// ActivityLastDayHeatmap - Compact heatmap for camera details screen
// ============================================================================

const BAR_WIDTH_NATIVE = 10;
const BAR_WIDTH_WEB = 9;
const BAR_WIDTH = isWeb ? BAR_WIDTH_WEB : BAR_WIDTH_NATIVE;
const BAR_HEIGHT = 31;
const BAR_GAP = 4; // theme.spacing.xs
const BAR_RADIUS = 2; // theme.spacing.xxs
const SVG_WIDTH = HOURS.length * BAR_WIDTH + (HOURS.length - 1) * BAR_GAP;
const BAR_TRANSFORMS = HOURS.map((hour) => `translate(${hour * (BAR_WIDTH + BAR_GAP)}, 0)`);

type ActivityLastDayHeatmapProps = {
  siteId: string;
  cameraKey: string;
  timezone: string;
};

export const ActivityLastDayHeatmap = memo(({ siteId, cameraKey, timezone }: ActivityLastDayHeatmapProps) => {
  const { theme } = useUnistyles();
  const now = useMemo(() => toZonedTime(new Date(), timezone), [timezone]);
  const yesterday = useMemo(() => subDays(now, 1), [now]);
  const currentHour = getHours(now);

  const { hourBuckets: todayBuckets, isLoading: isLoadingToday } = useDayClipCounts({
    siteId,
    cameraKey,
    selectedDate: now,
    timezone,
  });
  const { hourBuckets: yesterdayBuckets, isLoading: isLoadingYesterday } = useDayClipCounts({
    siteId,
    cameraKey,
    selectedDate: yesterday,
    timezone,
  });

  const hourCountMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const hour of HOURS) {
      // For hours <= currentHour, use today's data; for hours > currentHour, use yesterday's data
      const bucket = hour <= currentHour ? todayBuckets[hour] : yesterdayBuckets[hour];
      map.set(hour, bucket?.count ?? 0);
    }
    return map;
  }, [todayBuckets, yesterdayBuckets, currentHour]);

  const activityColors: ActivityColors = useMemo(
    () => ({
      none: theme.colors.surface1.foregroundTertiary,
      low: theme.colors.danger[400],
      high: theme.colors.danger[700],
    }),
    [theme],
  );

  const barFills = useMemo(
    () =>
      HOURS.map((hour) => {
        const clipCount = hourCountMap.get(hour) ?? 0;
        const activityLevel = getActivityLevel({ clipCount });
        return getActivityColor({ activityLevel, colors: activityColors });
      }),
    [hourCountMap, activityColors],
  );

  const isLoading = isLoadingToday || isLoadingYesterday;
  const barOpacity = isLoading ? 0.5 : 1;

  return (
    <View mt="xl" pr="sm">
      <Svg width={SVG_WIDTH} height={BAR_HEIGHT}>
        {HOURS.map((hour) => (
          <Rect
            key={hour}
            transform={BAR_TRANSFORMS[hour]}
            width={BAR_WIDTH}
            height={BAR_HEIGHT}
            rx={BAR_RADIUS}
            ry={BAR_RADIUS}
            fill={barFills[hour]}
            opacity={barOpacity}
          />
        ))}
      </Svg>
    </View>
  );
});

const styles = StyleSheet.create((theme: any) => ({
  scrollView: {
    paddingBottom: theme.spacing["7xl"],
    paddingTop: theme.spacing["5xl"],
    paddingLeft: theme.spacing.xs,
  },
}));
